import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2, CheckCircle2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WORDS = { list: "List", note: "Note", post: "Post", comment: "Comment", about: "Profile bio", dm: "Private message" };
const VIS = { public: "Everyone", friends: "Friends only", private: "Private" };
const DONE = { hidden: "Hidden", restored: "Put back", deleted: "Deleted", dismissed: "No action needed" };

function when(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

function ReportCard({ g, onChanged }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sure, setSure] = useState(false);
  const key = `${g.kind}:${g.item_id}`;

  const act = async (what, done) => {
    setBusy(true);
    try {
      await api.post(`/admin/reports/${g.kind}/${g.item_id}/${what}`, { note });
      toast.success(done);
      onChanged();
    } catch (e) { toast.error(errText(e, "That did not work. Please try again.")); }
    finally { setBusy(false); setSure(false); }
  };

  const btn = "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium disabled:opacity-50";
  return (
    <li className="rounded-2xl border border-border bg-card p-4" data-testid="report-card" data-key={key}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="rounded-full bg-[#C89B3C]/20 px-3 py-0.5 font-semibold text-[#C89B3C]" data-testid="report-kind">{WORDS[g.kind] || g.kind}</span>
        {g.visibility && g.kind !== "dm" && <span className="text-muted-foreground" data-testid="report-vis">{VIS[g.visibility] || g.visibility}</span>}
        <span className="text-muted-foreground">by {g.owner ? <Link to={`/u/${g.owner}`} className="underline" data-testid="report-owner">{g.owner}</Link> : "(unknown)"}</span>
        {g.hidden && !g.handled && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-600">Hidden</span>}
        {!g.exists && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-xs text-red-500" data-testid="report-gone">Gone</span>}
      </div>

      <blockquote className="mt-3 whitespace-pre-wrap break-words border-l-2 border-[#C89B3C] pl-3 text-sm" data-testid="report-excerpt">
        {g.excerpt || "(nothing to show)"}
      </blockquote>
      {g.kind === "dm" && <p className="mt-1 text-xs text-muted-foreground">Only the reported message is shown, not the rest of the conversation.</p>}

      {g.photos?.length > 0 && (
        <p className="mt-2 text-sm" data-testid="report-photos">
          Photos (open one only if you need to):{" "}
          {g.photos.map((u, i) => (
            <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="mr-3 underline">Photo {i + 1}</a>
          ))}
        </p>
      )}

      <div className="mt-3 text-sm" data-testid="report-reasons">
        <p className="font-medium">{g.reports.length === 1 ? "Reported once" : `Reported ${g.reports.length} times`}</p>
        <ul className="mt-1 space-y-0.5 text-muted-foreground">
          {g.reports.map((r, i) => (
            <li key={i}><span className="text-foreground">{r.reporter}</span>, {when(r.created_at)}: {r.reason || "(no reason given)"}</li>
          ))}
        </ul>
      </div>

      {g.handled ? (
        <div className="mt-3 border-t border-border pt-3 text-sm" data-testid="report-done">
          <p><span className="font-medium">{DONE[g.action] || "Handled"}</span> <span className="text-muted-foreground">{when(g.handled_at)}</span></p>
          {g.note && <p className="text-muted-foreground">Note: {g.note}</p>}
          {g.hidden && g.exists && (
            <button type="button" disabled={busy} onClick={() => act("restore", "Put back")} data-testid="report-restore" className={`${btn} mt-2 border-border`}>
              <Eye className="h-4 w-4" /> Put it back
            </button>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <input aria-label="Note for your records" data-testid="report-note" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for your records, e.g. why you did this" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          {!sure ? (
            <div className="flex flex-wrap gap-2">
              {g.exists && !g.hidden && (
                <button type="button" disabled={busy} onClick={() => act("hide", "Hidden")} data-testid="report-hide" className={`${btn} border-amber-500 text-amber-600`}>
                  <EyeOff className="h-4 w-4" /> Hide
                </button>
              )}
              {g.exists && g.hidden && (
                <button type="button" disabled={busy} onClick={() => act("restore", "Put back")} data-testid="report-restore" className={`${btn} border-border`}>
                  <Eye className="h-4 w-4" /> Put it back
                </button>
              )}
              {g.can_delete && (
                <button type="button" disabled={busy} onClick={() => setSure(true)} data-testid="report-delete" className={`${btn} border-red-500 text-red-500`}>
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" disabled={busy} onClick={() => act("dismiss", "Report closed")} data-testid="report-dismiss" className={`${btn} border-border`}>
                <CheckCircle2 className="h-4 w-4" /> No action needed
              </button>
            </div>
          ) : (
            <div className="rounded-xl border border-red-500/50 bg-red-500/10 p-3 text-sm" data-testid="report-confirm">
              <p className="font-medium">Delete this for good?</p>
              <p className="text-muted-foreground">It, its photos, comments and reactions are removed and cannot be brought back. The report stays as your record, with a copy of the words.</p>
              <div className="mt-2 flex gap-2">
                <button type="button" disabled={busy} onClick={() => act("delete", "Deleted")} data-testid="report-delete-yes" className={`${btn} border-red-500 bg-red-500 text-white`}>Yes, delete it</button>
                <button type="button" disabled={busy} onClick={() => setSure(false)} data-testid="report-delete-no" className={`${btn} border-border`}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** Only for the site owner. The server refuses anyone else, so this page just shows "not available" to them. */
export default function AdminReports({ embedded = false, onCount }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => { if (!embedded) document.title = "Reports | My Theatre"; }, [embedded]);

  const load = useCallback(() => {
    if (!user) return;
    api.get(all ? "/admin/reports?all=true" : "/admin/reports")
      .then((r) => { setDenied(false); setRows(r.data.reports || []); })
      .catch((e) => { if ([401, 403, 404].includes(e?.response?.status)) setDenied(true); else { setRows([]); toast.error("Could not load the reports."); } });
  }, [user, all]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (onCount && rows) onCount(rows.filter((g) => !g.handled).length); }, [rows, onCount]);

  if (!user || denied) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-12" data-testid="reports-denied">
        <h1 className="font-serif text-2xl font-semibold">Not available</h1>
        <p className="mt-2 text-muted-foreground">This page is for the site owner. {user ? "" : "Please sign in."}</p>
      </main>
    );
  }
  const open = (rows || []).filter((g) => !g.handled).length;
  const Wrap = embedded ? "div" : "main";
  return (
    <Wrap className={embedded ? "" : "mx-auto max-w-3xl px-4 py-8"} data-testid="reports-page">
      <h1 className="flex items-center gap-2 font-serif text-2xl font-semibold"><ShieldAlert className="h-6 w-6 text-[#C89B3C]" /> Reports</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Hide first, then delete once you have decided. A hidden item can be put back, and only its author can still see it.
        If you ever see anything that looks like child abuse imagery, do not keep or share it: hide it and report it to the police or the Internet Watch Foundation.
      </p>
      <label className="mt-4 flex items-center gap-2 text-sm">
        <input type="checkbox" data-testid="reports-all" checked={all} onChange={(e) => { setRows(null); setAll(e.target.checked); }} />
        Include reports I have already handled
      </label>

      {rows === null ? <p className="mt-6 text-muted-foreground">Loading…</p> : (
        <>
          <p className="mt-6 font-medium" data-testid="reports-count">{open === 0 ? "No open reports." : `${open} open report${open === 1 ? "" : "s"}`}</p>
          <ul className="mt-3 space-y-4">
            {rows.map((g) => <ReportCard key={`${g.kind}:${g.item_id}`} g={g} onChanged={load} />)}
          </ul>
        </>
      )}
    </Wrap>
  );
}